import { useI18nContext } from "@i18n/i18n-react";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { cn } from "@shadcn/lib/utils";
import { KeyboardAwareDrawer, KeyboardAwareDrawerContent } from "@components/KeyboardAwareDrawer";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@shadcn/ui/collapsible";
import { DrawerTitle } from "@shadcn/ui/drawer";
import { useNavigate, useLocation } from "@tanstack/react-router";
import { formatToCurrency } from "@utils/currency";
import { getDiscountFraction, isDiscountName, stripDiscountSuffix } from "@utils/discount";
import { isMedicalCategory } from "@utils/consultation";
import { ChevronDown, MessageSquareText } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";
import { ProductDrawerFragment$key } from "./__generated__/ProductDrawerFragment.graphql";
import { AddToBagButton } from "./AddToBagButton";
import { ProductTrack } from "../ProductTrack";
import { ProductTrackFragment$key } from "../__generated__/ProductTrackFragment.graphql";

const fragment = graphql`
	fragment ProductDrawerFragment on HubProduct {
		id
		priceAmount
		stock
		product {
			id
			name
			description
			photo {
				id
				variant(variant: PUBLIC)
			}
			brand {
				id
				name
				organization {
					id
					name
					imprint
				}
			}
			category {
				id
				name
				i18N
			}
		}
		stock
		priceAmount
	}
`;

type ProductDrawerProps = {
	hubId: string;
	product: ProductDrawerFragment$key;
	products?: ProductTrackFragment$key;
	onClose?: () => void;
};

export const ProductDrawer = (props: ProductDrawerProps) => {
	const { product: productKey, onClose, products } = props;

	const { id, product, priceAmount, stock } = useFragment(fragment, productKey);
	const [isOpen, setIsOpen] = useState(true);
	const [isImprintOpen, setIsImprintOpen] = useState(false);
	const navigate = useNavigate();
	const location = useLocation();
	const i18n = useI18nContext().LL["new-ui"];
	const isDiscounted = isDiscountName(product.name);
	const ogName = useMemo(() => stripDiscountSuffix(product.name), [product.name]);
	const ogPrice = useMemo(() => {
		if (!isDiscounted) {
			return priceAmount;
		}

		const discountFrac = getDiscountFraction(product.name) ?? 1;
		return priceAmount / (1 - discountFrac);
	}, [isDiscounted, priceAmount, product.name]);

	const isOnFinishPage = location.pathname.includes("/pick-up/finish");
	const hubMetaName = location.pathname.split("/")[1];

	useEffect(() => {
		const mediaQuery = window.matchMedia("(min-width: 1024px)");
		const syncImprintState = () => {
			setIsImprintOpen(mediaQuery.matches);
		};

		syncImprintState();
		mediaQuery.addEventListener("change", syncImprintState);

		return () => {
			mediaQuery.removeEventListener("change", syncImprintState);
		};
	}, []);

	return (
		<KeyboardAwareDrawer
			open={isOpen}
			onOpenChange={(open) => {
				setIsOpen(open);
				if (!open && onClose) {
					onClose();
				}
			}}
			shouldScaleBackground
		>
			<KeyboardAwareDrawerContent className="max-h-full! h-full overflow-hidden border-t-0 bg-slate-50">
				<VisuallyHidden>
					<DrawerTitle>Product Detail Page {ogName}</DrawerTitle>
				</VisuallyHidden>
				<div className="pointer-events-all -mt-6 h-full overflow-y-auto bg-slate-50 pb-24 lg:pb-40">
					<div className="mx-auto flex w-full max-w-[690px] flex-col gap-4">
						<img
							src={product.photo?.variant ?? "https://ui.shadcn.com/placeholder.svg"}
							alt={ogName}
							className="h-88 w-full object-cover lg:mt-6"
						/>
						<div className="space-y-1.5 px-6 py-4">
							{product.brand?.name ? (
								<p className="hidden font-breit text-lg font-medium leading-none text-muted-foreground lg:block">{product.brand.name}</p>
							) : null}
							<h2 className="font-breit text-2xl leading-8 text-card-foreground">{ogName}</h2>
							<div className="flex flex-wrap items-center gap-x-1 gap-y-2">
								<p className="font-breit text-base font-semibold leading-6 text-accent-foreground">{formatToCurrency(priceAmount)}</p>
								{isDiscounted ? (
									<div className="font-breit text-base font-normal leading-6 text-muted-foreground line-through">
										{formatToCurrency(ogPrice)}
									</div>
								) : null}
							</div>
							<p className="whitespace-pre-wrap text-sm leading-5 text-muted-foreground">{product.description}</p>
							{isMedicalCategory(product.category) && hubMetaName && (
								<button
									type="button"
									onClick={() => {
										setIsOpen(false);
										navigate({ to: "/$hubMetaName/consultation", params: { hubMetaName } });
									}}
									className="mt-2 flex items-center gap-2 text-left"
								>
									<MessageSquareText className="size-5 shrink-0 text-primary" />
									<span className="font-breit text-base leading-none text-primary">{i18n.CONSULTATION_PRODUCT_LINK()}</span>
								</button>
							)}
						</div>
						{products ? (
							<ProductTrack
								products={products}
								title={i18n.MORE_PRODUCTS()}
								className="[&>div]:mb-6 lg:pt-4 lg:pb-6 [&>div]:lg:mx-0 [&>div]:lg:max-w-none [&>div]:lg:mb-0 [&>div+div]:lg:mt-6"
							/>
						) : null}
						{product.brand.organization.imprint && (
							<Collapsible open={isImprintOpen} onOpenChange={setIsImprintOpen} className="px-5 py-4 lg:px-5">
								<CollapsibleTrigger className="flex items-center rounded-md text-left outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
									<span className="font-breit text-lg font-normal leading-7 text-primary">{i18n.PRODUCT_IMPRINT_TITLE()}</span>
									<span className="flex size-9 items-center justify-center rounded-md">
										<ChevronDown className={cn("h-4 w-4 text-primary transition-transform duration-200", isImprintOpen && "rotate-180")} />
									</span>
								</CollapsibleTrigger>
								<CollapsibleContent className="pt-4">
									<p className="whitespace-pre-wrap font-sans text-sm font-normal leading-5 text-muted-foreground">
										{product.brand.organization.imprint}
									</p>
								</CollapsibleContent>
							</Collapsible>
						)}
						{/* <h2 className="text-2xl text-neutral-900 font-breit pt-4 px-6">Recommended.</h2>
						<Suspense>
							<RecommendedProducts hubId={hubId} />
						</Suspense> */}
					</div>
				</div>
				<div className="fixed bottom-0 left-0 right-0 z-10 m-6 md:static md:m-6 md:w-full lg:fixed lg:bottom-8 lg:left-1/2 lg:right-auto lg:m-0 lg:w-[690px] lg:max-w-[calc(100vw-3rem)] lg:-translate-x-1/2">
					<AddToBagButton
						hubProductId={id}
						price={priceAmount}
						stock={stock > 1 && priceAmount === 0 ? 1 : stock}
						onClick={() => {
							if (isOnFinishPage && hubMetaName) {
								// Navigate immediately to avoid flash
								navigate({
									to: "/$hubMetaName/instant-buy",
									params: { hubMetaName },
									search: { tab: "recommended" },
								});
								// Then close drawer after navigation
								setTimeout(() => {
									setIsOpen(false);
								}, 300);
							} else {
								setIsOpen(false);
								if (onClose) {
									setTimeout(onClose, 300);
								}
							}
						}}
						disabled={stock === 0}
					/>
				</div>
			</KeyboardAwareDrawerContent>
		</KeyboardAwareDrawer>
	);
};
